import {Component, OnDestroy, OnInit} from '@angular/core';
import {IDialogView} from '@shared/components/ok-cancel-dialog/ok-cancel-dialog.component';
import {DialogModel} from '@shared/models/dialog.model';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {Dac6ReporterApiService} from '../../../services/dac6-reporter.service';
import {AddArrangementChartRecordCommand} from '../../../commands/dac6-reporter.command';
import {IQueryState} from '@shared/store/reducers/query.reducer';
import {select, Store} from '@ngrx/store';
import {QueryProvider} from '@shared/queries/query-provider';
import {takeUntil} from 'rxjs/operators';
import {getQueryResponse} from '@shared/store/selectors/query.selector';
import {Subject} from 'rxjs';
import {CrossFieldErrorMatcher} from '@shared/models/forms.models';

@Component({
    standalone: false,
    selector: 'particle-add-arrangment-chart-record-dialog',
  templateUrl: './add-arrangment-chart-record-dialog.component.html',
  styleUrls: ['./add-arrangment-chart-record-dialog.component.scss']
})
export class AddArrangmentChartRecordDialogComponent implements OnInit, IDialogView, OnDestroy {
  data: DialogModel;
  recordForm: FormGroup;
  recordFormError: any;
  entities: any[];
  formId: string;
  errorMatcher = new CrossFieldErrorMatcher();
  private ngUnsubscribe$ = new Subject<void>();
  countryList = [];
  releventTaxpayers = [];
  constructor(private formBuilder: FormBuilder,
              private queryStore: Store<IQueryState>,
              private dac6ReporterApiService: Dac6ReporterApiService) {
    this.recordForm = this.formBuilder.group({
      selectedEntity: ['', Validators.required],
      recordType: ['', Validators.required],
      personForm: this.formBuilder.group({
        FirstName_6_p: [''],
        LastName_6_p: [''],
        DateOfBirth_6_p: [''],
        PlaceOfBirth_6_p: [''],
        ResidenceCountryCode_11_p: ['']
      }),
      organisationForm: this.formBuilder.group({
        OrganisationName_6_p: [''],
        EntityCode: [''],
        ResidenceCountryCode_12_p: ['']
      }),
      Ownership_1_p: ['', this.validateOwnership.bind(this)],
      InvestmentAmount_1_p: ['', this.validateInvestAmount]
    });
    this.recordFormError = {
      selectedEntity: {},
      recordType: {},
      personForm: {
        FirstName_6_p: {},
        LastName_6_p: {},
        DateOfBirth_6_p: {},
        PlaceOfBirth_6_p: {},
        ResidenceCountryCode_11_p: {}
      },
      organisationForm: {
        OrganisationName_6_p: {},
        ResidenceCountryCode_12_p: {},
      },
      Ownership_1_p: {},
      InvestmentAmount_1_p: {}
    };
  }

  ngOnInit() {
    this.dac6ReporterApiService.getAllArrangementChartEntities().subscribe((response: any[]) => {
      if (response) {
        this.entities = response;
        this.formId = response[0].formId;
        const parentEntity = response.find(o => o.isParent === true);
        if (parentEntity) {
          this.recordForm.patchValue({
            selectedEntity: parentEntity.id
          });
        } else {
          this.recordForm.patchValue({
            selectedEntity: response[0].id
          });
        }
      }
    });
    this.queryStore.pipe(takeUntil(this.ngUnsubscribe$), select(getQueryResponse, 'Dac6-CountryCode-Config')).subscribe(response => {
      if (response && response.items) {
        this.countryList = response.items;
      }
    });
    this.queryStore.pipe(takeUntil(this.ngUnsubscribe$), select(getQueryResponse, 'AllRelevantTaxPayers')).subscribe(response => {
      if (response && response.items) {
        this.releventTaxpayers = response.items;
      }
    });
    this.recordForm.valueChanges.subscribe(value => {
      this.onFormValuesChanged(this.recordForm, this.recordFormError);
    });
  }
  onOkClicked(): boolean {
    let fieldValues = {};
    if (this.recordForm.value.recordType === 'Person') {
      fieldValues = this.recordForm.value.personForm;
    } else if (this.recordForm.value.recordType === 'Organisation') {
      fieldValues = this.recordForm.value.organisationForm;
    }
    if (this.recordForm.value['InvestmentAmount_1_p']) {
      fieldValues['InvestmentAmount_1_p'] = this.recordForm.value['InvestmentAmount_1_p'];
    }
    if (this.recordForm.value['Ownership_1_p']) {
      fieldValues['Ownership_1_p'] = this.recordForm.value['Ownership_1_p'];
    }
    const command = new AddArrangementChartRecordCommand(fieldValues,
                                                         this.recordForm.value.selectedEntity,
                                                         this.recordForm.value.recordType,
                                                         this.formId,
                                                         this.recordForm.value.entityCode);
    this.data.input['addRecordCommand'] = command;
    return true;
  }
  onFormValuesChanged(form, formError) {
    for (const field in formError) {
      if (!formError.hasOwnProperty(field)) {
        continue;
      }
      if (field === 'personForm' || field === 'organisationForm') {
        this.onFormValuesChanged(this.recordForm.get(field), this.recordFormError[field]);
        continue;
      }
      formError[field] = {};
      const control = form.get(field);
      if (control && control.dirty && !control.valid) {
        formError[field] = control.errors;
      }
    }
    this.data.isDisabledOkButton = this.recordForm.invalid;
  }
  onRecordTypeChanged(event) {
    if (this.countryList.length === 0) {
      QueryProvider.invokeQuery(this.queryStore, 'Dac6-CountryCode-Config', {});
    }
    if (event.value == 'Person') {
      this.clearFormValidators(this.recordForm.get('organisationForm'), this.recordFormError.organisationForm);
      for (const field in this.recordFormError.personForm) {
        this.recordForm.get('personForm').get(field).setValidators([Validators.required]);
        this.recordForm.get('personForm').get(field).updateValueAndValidity();
      }
    } else if (event.value == 'Organisation') {
      this.clearFormValidators(this.recordForm.get('personForm'), this.recordFormError.personForm);
      for (const field in this.recordFormError.organisationForm) {
        this.recordForm.get('organisationForm').get(field).setValidators([Validators.required]);
        this.recordForm.get('organisationForm').get(field).updateValueAndValidity();
      }
      if (this.releventTaxpayers.length === 0) {
        QueryProvider.invokeQuery(this.queryStore, 'AllRelevantTaxPayers', {});
      }
    }
  }
  onSelectRTP(event: any) {
    const selectedRtp = this.releventTaxpayers.find(item => item.entityName === event.value);
    if (selectedRtp.countryOfResidence) {
      this.recordForm.patchValue({
        organisationForm : {
          ResidenceCountryCode_12_p : selectedRtp.countryOfResidence
        }
      });
    }
    if (selectedRtp.entityCode) {
      this.recordForm.patchValue({
        organisationForm : {
          EntityCode : selectedRtp.entityCode
        }
      });
    }
  }
  clearFormValidators(form: any, subForm) {
    for (const field in subForm) {
      form.get(field).clearValidators();
      form.get(field).updateValueAndValidity();
    }
  }
  validateOwnership(form: FormControl) {
    const formValue = form.value ? form.value.toString() : '';
    const hasAlphabet = formValue.split('').filter(ch => isNaN(ch * 1));
    if (hasAlphabet.length) {
      return {
        containsAlphabet: true
      };
    }
    if (Number(formValue) > 100) {
      return {
        invalidOwnership: true
      };
    }
    return null;
  }
  validateInvestAmount(form: FormControl) {
    const formValue = form.value ? form.value.toString() : '';
    const hasAlphabet = formValue.split('').filter(ch => isNaN(ch * 1));
    if (hasAlphabet.length) {
      return {
        containsAlphabet: true
      };
    }
    return null;
  }
  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }

}
